<template>
  <div class="view quiz">

    <!-- Gender screen -->
    <transition name="slide" mode="out-in">
      <div v-if="!genderSelected" key="gender" class="quiz__screen">
        <p class="quiz__step orbitron">PASUL 2 / 3 — PROFILUL TĂU</p>
        <h2 class="quiz__question orbitron">Ești...?</h2>

        <div class="gender-cards">
          <button
            class="gender-card"
            :class="{ 'gender-card--active': pendingGender === 'male' }"
            @click="pendingGender = 'male'"
          >
            <span class="gender-card__icon">♂</span>
            <span class="gender-card__label orbitron">BĂIAT</span>
          </button>
          <button
            class="gender-card"
            :class="{ 'gender-card--active': pendingGender === 'female' }"
            @click="pendingGender = 'female'"
          >
            <span class="gender-card__icon">♀</span>
            <span class="gender-card__label orbitron">FATĂ</span>
          </button>
        </div>

        <button class="neon-btn" :disabled="!pendingGender" @click="confirmGender">
          CONTINUĂ
        </button>
      </div>

      <!-- Quiz questions -->
      <div v-else key="quiz" class="quiz__screen">
        <div class="quiz__progress-bar">
          <div class="quiz__progress-fill" :style="{ width: progressPct + '%' }"></div>
        </div>
        <p class="quiz__step orbitron">ÎNTREBAREA {{ currentIndex + 1 }} / {{ questions.length }}</p>

        <transition name="question-slide" mode="out-in">
          <div :key="currentIndex" class="quiz__q-block">
            <h2 class="quiz__question orbitron">{{ currentQuestion.question }}</h2>
            <div class="answer-grid">
              <button
                v-for="(ans, i) in currentQuestion.answers"
                :key="i"
                class="answer-card glass-card"
                :class="{ 'answer-card--selected': selectedAnswer === i }"
                @click="selectAnswer(i)"
              >
                <span class="answer-card__emoji">{{ ans.emoji }}</span>
                <span class="answer-card__text">{{ ans.text }}</span>
              </button>
            </div>
          </div>
        </transition>

        <button class="neon-btn" :disabled="selectedAnswer === null" @click="nextQuestion">
          {{ isLast ? 'DEZVĂLUIE REZULTATUL' : 'CONTINUĂ' }}
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useSessionStore } from '@/store/session'
import { QUIZ_QUESTIONS, calculatePersona } from '@/data/questions'

const router = useRouter()
const session = useSessionStore()

const questions = QUIZ_QUESTIONS
const genderSelected = ref(false)
const pendingGender = ref(null)
const currentIndex = ref(0)
const selectedAnswer = ref(null)
const answers = ref([])

const currentQuestion = computed(() => questions[currentIndex.value])
const isLast = computed(() => currentIndex.value === questions.length - 1)
const progressPct = computed(() => ((currentIndex.value) / questions.length) * 100)

function confirmGender() {
  session.gender = pendingGender.value
  genderSelected.value = true
}

function selectAnswer(i) {
  selectedAnswer.value = i
}

function nextQuestion() {
  if (selectedAnswer.value === null) return
  answers.value.push(currentQuestion.value.answers[selectedAnswer.value])
  selectedAnswer.value = null

  if (isLast.value) {
    session.quizAnswers = answers.value
    session.personaId = calculatePersona(answers.value)
    router.push('/processing')
  } else {
    currentIndex.value++
  }
}
</script>

<style scoped>
.quiz {
  padding: 2.5rem 4rem;
  gap: 1.5rem;
}

.quiz__screen {
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

.quiz__progress-bar {
  width: 100%;
  height: 3px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
}

.quiz__progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #7c3aed, #60a5fa);
  transition: width 0.4s ease;
  box-shadow: 0 0 8px rgba(139, 92, 246, 0.7);
}

.quiz__step {
  font-size: 0.8rem;
  letter-spacing: 0.4em;
  color: rgba(139, 92, 246, 0.7);
}

.quiz__question {
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 800;
  letter-spacing: 0.05em;
  text-align: center;
  color: #fff;
  text-shadow: 0 0 30px rgba(139, 92, 246, 0.5);
}

/* ─── Gender cards ─── */
.gender-cards {
  display: flex;
  gap: 3rem;
}

.gender-card {
  width: 220px;
  height: 220px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.04);
  border: 2px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.gender-card:active,
.gender-card--active {
  border-color: rgba(139, 92, 246, 0.8);
  background: rgba(139, 92, 246, 0.12);
  box-shadow: 0 0 40px rgba(139, 92, 246, 0.3);
}

.gender-card__icon {
  font-size: 4rem;
  color: rgba(255, 255, 255, 0.7);
}

.gender-card--active .gender-card__icon { color: #fff; }

.gender-card__label {
  font-size: 1.2rem;
  letter-spacing: 0.2em;
  color: rgba(255, 255, 255, 0.5);
}

.gender-card--active .gender-card__label { color: #fff; }

/* ─── Answer grid ─── */
.quiz__q-block {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.answer-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  width: 100%;
}

.answer-card {
  padding: 1.5rem 1rem;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.25s ease;
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.06);
  min-height: 140px;
}

.answer-card:active,
.answer-card--selected {
  border-color: rgba(139, 92, 246, 0.8);
  background: rgba(139, 92, 246, 0.12);
  box-shadow: 0 0 30px rgba(139, 92, 246, 0.25);
  transform: translateY(-3px);
}

.answer-card__emoji {
  font-size: 2.2rem;
}

.answer-card__text {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  letter-spacing: 0.05em;
}

.answer-card--selected .answer-card__text { color: #fff; }

.neon-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.slide-enter-active,
.slide-leave-active { transition: all 0.35s ease; }
.slide-enter-from { opacity: 0; transform: translateX(30px); }
.slide-leave-to { opacity: 0; transform: translateX(-30px); }

.question-slide-enter-active,
.question-slide-leave-active { transition: all 0.3s ease; }
.question-slide-enter-from { opacity: 0; transform: translateY(20px); }
.question-slide-leave-to { opacity: 0; transform: translateY(-20px); }
</style>
